"use client";

import {
  Criterion,
  DeleteCriteriaRequest,
  ExtractCriteriaRequest,
  MindstringPrototype,
} from "@/generated/fastapi";
import { useCriteriaBackend } from "@/features/criteria";
import { showErrorToast } from "@/shared/feedback";
import useSearchCriteriaSelection from "./useSearchCriteriaSelection";

interface UseSearchCriteriaActionsProps {
  targetId?: string | null;
  prototypeCategory?: MindstringPrototype | null;
}

export default function useSearchCriteriaActions({
  targetId,
  prototypeCategory,
}: UseSearchCriteriaActionsProps) {
  const { pathname, selectedCriteria, removeCriteria, restoreCriteria } =
    useSearchCriteriaSelection();
  const {
    criteria,
    handleDeleteCriteria,
    handleExtractCriteria,
  } = useCriteriaBackend({
    targetId,
    prototypeCategory,
  });

  async function extractCriteria(request: ExtractCriteriaRequest) {
    try {
      return await handleExtractCriteria(request);
    } catch {
      showErrorToast("Failed to extract criteria");
      return null;
    }
  }

  async function deleteCriteria(request: DeleteCriteriaRequest) {
    const criterionIds = request.criterion_ids || [];

    const toRemove = criterionIds
      .map((id) => selectedCriteria.find((criterion) => criterion.id === id))
      .filter((criterion): criterion is Criterion => Boolean(criterion));

    const previousSelection = selectedCriteria;

    if (toRemove.length > 0) {
      removeCriteria(toRemove);
    }

    try {
      return await handleDeleteCriteria(request);
    } catch {
      if (pathname) {
        restoreCriteria(previousSelection);
      }
      showErrorToast("Failed to delete criteria");
      return false;
    }
  }

  return {
    criteria,
    extractCriteria,
    deleteCriteria,
  };
}
